import { telemetry } from './bus';

/**
 * Performance instrumentation per COMPLETION_PLAN_v3.md §6.6 / §6.8.
 * Emits perf.fps.sample / perf.lcp / perf.cls / perf.inp.
 * Safe to call from main.tsx; bails on browsers missing the relevant APIs.
 */
export function installPerfObservers() {
  if (typeof window === 'undefined' || typeof PerformanceObserver === 'undefined') return;

  // ---- FPS sampler (1s window via rAF) ----
  let frames = 0;
  let last = performance.now();
  function tick(now: number) {
    frames++;
    if (now - last >= 1000) {
      telemetry.emit({
        type: 'perf.fps.sample',
        scene: (document.querySelector('[data-game]') as HTMLElement | null)?.dataset.game ?? 'shell',
        fps: frames,
        ts: now,
      });
      frames = 0;
      last = now;
    }
    requestAnimationFrame(tick);
  }
  requestAnimationFrame(tick);

  // ---- LCP ----
  try {
    new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lcp = entries[entries.length - 1] as PerformanceEntry & { startTime: number };
      if (lcp) telemetry.emit({ type: 'perf.lcp', ms: lcp.startTime, ts: performance.now() });
    }).observe({ type: 'largest-contentful-paint', buffered: true });
  } catch { /* ignore */ }

  // ---- CLS ----
  try {
    let cls = 0;
    new PerformanceObserver((list) => {
      for (const e of list.getEntries() as Array<PerformanceEntry & { value: number; hadRecentInput?: boolean }>) {
        if (!e.hadRecentInput) cls += e.value;
      }
      telemetry.emit({ type: 'perf.cls', value: cls, ts: performance.now() });
    }).observe({ type: 'layout-shift', buffered: true });
  } catch { /* ignore */ }

  // ---- INP via event timing (entries with long durations only) ----
  try {
    new PerformanceObserver((list) => {
      for (const e of list.getEntries() as Array<PerformanceEntry & { duration: number; interactionId?: number }>) {
        if (e.duration > 16 && e.interactionId) {
          telemetry.emit({ type: 'perf.event.duration', ms: e.duration, ts: performance.now() });
        }
      }
    }).observe({ type: 'event', buffered: true, durationThreshold: 16 } as PerformanceObserverInit);
  } catch { /* ignore */ }
}
